import { useMemo } from "react";
import {
  useQuery,
  useQueryClient,
  useMutation,
  useIsFetching,
} from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  getAllCustomerReturnRequestPermissions,
  getAllCustomerListUnderClient,
  updateCustomerReturnRequestPermission,
  postCustomerReturnRequestPermission,
  getGlobalReturnPermissions,
  postGlobalReturnPermissions,
  updateGlobalReturnPermissions,
} from "../../../services/customerService";

/**
 * @typedef {Object} UpdatePermissionData
 * @property {string} customerId
 * @property {string} permission
 */

const returnPermissionSettingsQueryKeys = {
  allCurrentSettings: ["customerReturnPermissions"],
  allCustomers: ["customerList"],
  globalReturn: ["globalReturnPermissions"],
};

export const returnPermissionKeys = {
  allowedReturns: "Allowed returns",
  allowedReturnByApproval: "Allowed returns by approval",
  notAllowedReturns: "Not allowed returns",
};

export const returnPermissionOptions = Object.entries(returnPermissionKeys).map(
  ([key, value]) => ({
    label: value,
    value: key,
  })
);

export const createdReturnPermissionKeys = {
  allowedReturnByApproval: "Allowed returns by approval",
  notAllowedReturns: "Not allowed returns",
};

export const createReturnPermissionsOptions = Object.entries(
  createdReturnPermissionKeys
).map(([key, value]) => ({
  label: value,
  value: key,
}));

export const accessorReturnPermissionSettingStatus = (originalRow) => {
  if (!originalRow?.isActive) return returnPermissionKeys.allowedReturns;

  return originalRow?.byApproval
    ? returnPermissionKeys.allowedReturnByApproval
    : returnPermissionKeys.notAllowedReturns;
};

export const filterReturnPermissionData = (data) => {
  if (!data) return [];

  return data.sort((a, b) => {
    if (a.customerId < b.customerId) return -1;
    if (a.customerId > b.customerId) return 1;
    return 0;
  });
};

export const useGetCustomerReturnRequestPermissions = () =>
  useQuery({
    queryKey: returnPermissionSettingsQueryKeys.allCurrentSettings,
    queryFn: getAllCustomerReturnRequestPermissions,
  });

const useGetCustomerReturnRequestPermissionsCacheData = () => {
  const queryClient = useQueryClient();

  const response = queryClient.getQueryData(
    returnPermissionSettingsQueryKeys.allCurrentSettings
  );

  const filterData = useMemo(
    () => filterReturnPermissionData(response?.data),
    [response?.data]
  );

  return filterData;
};

// Customer Return Permissions Settings

export const useGetAllCustomerWithoutPermissions = () => {
  const { data: response, isLoading } = useQuery({
    queryKey: returnPermissionSettingsQueryKeys.allCustomers,
    queryFn: getAllCustomerListUnderClient,
  });

  const latestCustomerReturnRequestPermissions =
    useGetCustomerReturnRequestPermissionsCacheData();

  const filterCustomerWithoutPermission = useMemo(() => {
    if (!response?.data) return [];

    if (!latestCustomerReturnRequestPermissions.length) return response.data;

    const customerIds = latestCustomerReturnRequestPermissions.map(
      (customer) => customer.customerId
    );

    const customerWithoutPermission = response.data.filter(
      (customer) => !customerIds.includes(customer.tranid)
    );

    const sortedByCustomerName = customerWithoutPermission.sort(
      (current, next) => {
        const currentCustomer = current.customer?.toLowerCase().trim();
        const nextCustomer = next.customer?.toLowerCase().trim();

        if (currentCustomer < nextCustomer) return -1;
        if (currentCustomer > nextCustomer) return 1;
        return 0;
      }
    );

    return sortedByCustomerName;
  }, [response?.data, latestCustomerReturnRequestPermissions]);

  return { data: filterCustomerWithoutPermission, isLoading };
};

export const useCreateCustomerReturnPermission = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading, isError } = useMutation({
    mutationFn: postCustomerReturnRequestPermission,
  });

  const createMutation = async (batchData) => {
    if (!batchData.length) return;

    toast.info("Creating permissions, please wait...", {
      toastId: "create-permission",
    });

    const statusesPromises = Promise.allSettled(
      batchData.map((data) => mutateAsync(data))
    );

    // wait
    const statuses = await statusesPromises;

    if (batchData.length === statuses?.length) {
      toast.dismiss("create-permission");
      queryClient.invalidateQueries();
    }

    if (statuses?.some((status) => status.status === "rejected")) {
      const numberOfFailed = statuses.filter(
        (status) => status.status === "rejected"
      ).length;

      toast.error(
        `${numberOfFailed}/${batchData.length} permissions failed to create`
      );
    }

    if (statuses?.some((status) => status.status === "fulfilled")) {
      const numberOfSuccess = statuses.filter(
        (status) => status.status === "fulfilled"
      ).length;

      toast.success(
        `Create ${numberOfSuccess}/${batchData.length} permissions successfully`
      );
    }
  };

  return { createMutation, isLoading, isError };
};

export const useUpdateCustomerReturnPermission = () => {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isLoading, isError } = useMutation({
    mutationFn: updateCustomerReturnRequestPermission,
    retry: 3,
  });

  /**
   * @param {UpdatePermissionData} data
   */

  const mutateUpdate = (data) => {
    mutate(data, {
      onSuccess: () => {
        queryClient.invalidateQueries(
          returnPermissionSettingsQueryKeys.allCurrentSettings
        );
        toast.success("Permission updated successfully");
      },
      onError: () => {
        toast.error("Failed to update permission");
      },
    });
  };

  /**
   * @param {UpdatePermissionData[]} batchData
   */

  const mutateBatchUpdate = async (batchData = []) => {
    if (!batchData.length) return;

    if (!Array.isArray(batchData)) {
      throw new Error("Batch data must be an array");
    }

    toast.info("Updating permissions, please wait...", {
      toastId: "update-permission",
    });

    const statusesPromises = Promise.allSettled(
      batchData.map((data) => mutateAsync(data))
    );

    const statuses = await statusesPromises;

    if (batchData.length === statuses?.length) {
      toast.dismiss("update-permission");

      queryClient.invalidateQueries(
        returnPermissionSettingsQueryKeys.allCurrentSettings
      );
    }

    if (statuses?.some((status) => status.status === "rejected")) {
      const numberOfFailed = statuses.filter(
        (status) => status.status === "rejected"
      ).length;

      toast.error(
        `${numberOfFailed}/${batchData.length} permissions failed to update`
      );
    }

    if (statuses?.some((status) => status.status === "fulfilled")) {
      const numberOfSuccess = statuses.filter(
        (status) => status.status === "fulfilled"
      ).length;

      toast.success(
        `Update ${numberOfSuccess}/${batchData.length} permissions successfully`
      );
    }
  };

  return { isLoading, isError, mutateUpdate, mutateBatchUpdate };
};

// Global Settings
export const useGetGlobalReturnPermissions = () =>
  useQuery({
    queryKey: returnPermissionSettingsQueryKeys.globalReturn,
    queryFn: getGlobalReturnPermissions,
  });

export const useGetGlobalReturnPermissionsCacheData = () => {
  const queryClient = useQueryClient();

  const response = queryClient.getQueryData(
    returnPermissionSettingsQueryKeys.globalReturn
  );

  return response?.data;
};

const onSuccessUpdateGlobalReturnPermissions = (queryClient) => () => {
  toast.success("Global return permission updated successfully");

  queryClient.invalidateQueries(returnPermissionSettingsQueryKeys.globalReturn);
};

const onErrorUpdateGlobalReturnPermissions = () => {
  toast.error("Failed to update global return permission");
};

export const useCreateGlobalReturnPermissions = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: postGlobalReturnPermissions,
    onSuccess: onSuccessUpdateGlobalReturnPermissions(queryClient),
    onError: onErrorUpdateGlobalReturnPermissions,
  });

  return mutation;
};

export const useUpdateGlobalReturnPermissions = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: updateGlobalReturnPermissions,
    onSuccess: onSuccessUpdateGlobalReturnPermissions(queryClient),
    onError: onErrorUpdateGlobalReturnPermissions,
  });

  return mutation;
};

export const useIsFetchingGlobalReturnPermissions = () =>
  useIsFetching({
    queryKey: returnPermissionSettingsQueryKeys.globalReturn,
  });
